/**
 * Cloudflare Turnstile widget hook (explicit render, interaction-only appearance).
 *
 * Same behaviour as the contact form's inline integration (src/pages/Contact.tsx): the script is
 * loaded once, the widget renders into `containerRef`, and `getToken()` does a bounded recovery
 * (reset + poll up to 8 s) when no token has arrived yet. With no VITE_TURNSTILE_SITE_KEY the hook
 * is inert and `getToken()` resolves null (the server then skips verification only when its own
 * secret is unset). Contact.tsx keeps its inline copy; new forms use this hook.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const RECOVERY_MS = 8000;

export interface UseTurnstile {
  enabled: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  error: string | null;
  getToken: () => Promise<string | null>;
  reset: () => void;
}

export function useTurnstile(messages: { failed: string; expired: string }): UseTurnstile {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  const enabled = Boolean(siteKey);
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const token = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { failed, expired } = messages;

  useEffect(() => {
    if (!siteKey) return undefined;
    let mounted = true;

    const render = () => {
      if (!mounted || !containerRef.current || !window.turnstile || widgetId.current) return;
      try {
        widgetId.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (t: string) => {
            if (!mounted) return;
            token.current = t;
            setError(null);
          },
          'error-callback': () => {
            if (!mounted) return;
            token.current = null;
            setError(failed);
          },
          'expired-callback': () => {
            if (!mounted) return;
            token.current = null;
            setError(expired);
          },
          theme: 'light',
          appearance: 'interaction-only',
        });
      } catch {
        if (mounted) setError(failed);
      }
    };

    if (window.turnstile) {
      render();
    } else {
      const existing = document.querySelector<HTMLScriptElement>('script[src*="challenges.cloudflare.com/turnstile"]');
      if (existing) {
        existing.addEventListener('load', render);
      } else {
        const s = document.createElement('script');
        s.src = SCRIPT_SRC;
        s.async = true;
        s.defer = true;
        s.addEventListener('load', render);
        document.head.appendChild(s);
      }
    }

    return () => {
      mounted = false;
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          // already removed
        }
      }
      widgetId.current = null;
    };
  }, [siteKey, failed, expired]);

  const reset = useCallback(() => {
    token.current = null;
    if (widgetId.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetId.current);
      } catch {
        // widget gone
      }
    }
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!siteKey) return null;
    if (token.current) return token.current;
    if (!widgetId.current || !window.turnstile) return null;
    reset();
    return new Promise<string | null>((resolve) => {
      const started = Date.now();
      const poll = window.setInterval(() => {
        if (token.current) {
          window.clearInterval(poll);
          resolve(token.current);
        } else if (Date.now() - started > RECOVERY_MS) {
          window.clearInterval(poll);
          resolve(null);
        }
      }, 250);
    });
  }, [siteKey, reset]);

  return { enabled, containerRef, error, getToken, reset };
}
